<script setup>
import {usePortalContentAndDesignStore} from "../../../../../../stores/partners/portalContentAndDesignStore";
import EditableResourceImageField
    from "../../../../portal-editable-fields/resources/EditableResourceImageField.vue";
import EditableTextField from "../../../../portal-editable-fields/EditableTextField.vue";
import EditableResourceTextEditorField
    from "../../../../portal-editable-fields/resources/EditableResourceTextEditorField.vue";
import {ref, computed} from "vue";
import { useHoverTracker} from "../../../../../../composables/partners/useHoverTracker";

const { hoverContentActive, isHoverContentActive } = useHoverTracker()

const props = defineProps({
    resource: {
        type: Object,
        required: true,
    },
});

const portalContentAndDesignStore = usePortalContentAndDesignStore();

const hoverMainImageContainer = ref(false);

const alignmentClass = computed(() => {
    switch (props.resource.alignment) {
        case 'center': return 'justify-center text-center';
        case 'right': return 'justify-end text-right';
        default: return 'justify-start text-left';
    }
});

const headerLayoutClass = computed(() => {
    const position = props.resource.main_image_position || 'left';
    const alignment = props.resource.alignment || 'left';

    if (position === 'top') {
        if (alignment === 'center') return 'flex flex-col items-center gap-4';
        if (alignment === 'right') return 'flex flex-col items-end gap-4';
        return 'flex flex-col items-start gap-4';
    }

    if (alignment === 'center') return 'flex flex-col items-center gap-4';
    return 'flex items-center gap-6';
});

const imageSizeClass = computed(() => {
    // Backwards compatibility: check for old retina resize setting
    if (props.resource.main_image_size === undefined && props.resource.main_image_retina_resize !== undefined) {
        return props.resource.main_image_retina_resize ? 'max-w-8' : 'max-w-16';
    }
    // New size setting
    const size = props.resource.main_image_size || 'medium';
    switch (size) {
        case 'small':
        case '@2x': return 'max-w-8';
        case 'large': return 'max-w-24';
        default: return 'max-w-16'; // medium
    }
});

</script>

<template>
    <div>
        <div :class="[headerLayoutClass, alignmentClass]"
             @mouseenter="hoverMainImageContainer = true"
             @mouseleave="hoverMainImageContainer = false"
        >
            <editable-resource-image-field
                 resource-image-path="main_image"
                 :resource-content="resource"
                 :img-class="imageSizeClass"
                 img-name="main_image"
                 :show-add-image-button="hoverMainImageContainer"
            />

            <div class="space-y-1.5">
                <editable-text-field
                    v-if="resource.title"
                    text-type="h3"
                    :class="['text-xl md:text-2xl font-semibold', portalContentAndDesignStore.getPageContentTextColorClass]"
                    v-model="resource.title"
                />
                <editable-resource-text-editor-field
                    v-if="resource.description"
                    :class="[
                        'partnero-page-content-section text-base md:text-lg text-opacity-80 space-y-1',
                        portalContentAndDesignStore.getPageContentTextColorClass
                    ]"
                    :resource="resource"
                    resource-field="description"
                />
            </div>
        </div>

        <div v-if="resource.contents?.length"
            :class="[
                  (resource.title || resource.description) ? 'mt-6' : '',
                  'grid gap-12',
                  resource.display_content
                    ? `md:grid-cols-${Math.min(resource.columns_count || 3, resource.contents.length)}`
                    : ''
                ]"
            >
            <div
                v-for="content in resource.contents"
                :key="content.unique_id"
                :id="content.unique_id"
                class="flex flex-col items-start space-y-2"
                @mouseenter="hoverContentActive(content.unique_id, true)"
                @mouseleave="hoverContentActive(content.unique_id, false)"
            >
                <editable-text-field
                    v-if="content.title"
                    text-type="custom"
                    class="text-md md:text-lg font-semibold"
                    v-model="content.title"
                ></editable-text-field>
                <editable-resource-image-field
                    :resource-content="content"
                    resource-image-path="image_resource"
                    :img-class="`w-auto rounded-sm ${(content.image_border ?? true) ? 'border' : ''}`"
                    img-name="image_resource"
                    :show-add-image-button="isHoverContentActive(content.unique_id)"
                />
            </div>
    </div>
    </div>
</template>
